import { createAction } from '@reduxjs/toolkit';
import { NavigateFunction } from 'react-router-dom';
import { toPostDataModel } from '../../mappers/posts/toPostDataModel';
import { ymTrackPostBookmark, ymTrackPostDislike, ymTrackPostLike } from '../../utils/metrika';
import { SharingMethod } from '../../utils/share';
import { setBusinessRecords } from '../business/actions';
import { setCategoryRecords } from '../categories/actions';
import { setCollectionsRecords } from '../collections/actions';
import { shareCollection } from '../collections/service';
import { showNotificationAsync } from '../notifications/actions';
import { UserModel } from '../user/types';
import { PostStatus } from './constants';
import { selectPostById, selectPostCollectionLoading } from './selectors';
import * as postsService from './service';
import {
    PostCollectionModel,
    ReactionState,
    type PostDataModel,
    type PostModel,
    type ReportReasons,
} from './types';
import type { EventSource } from '../../utils/analyticsService';
import type { RootState } from '../store';
import type { AnyAction } from 'redux';
import type { ThunkAction } from 'redux-thunk';

export const removePostRecords = createAction(
    '@@posts/REMOVE_POST_RECORDS',
    (...payload: string[]) => ({ payload }),
);

export const setPostRecords = createAction(
    '@@posts/SET_POST_RECORDS',
    (...payload: PostDataModel[]) => ({ payload }),
);

export const setPostCollection = createAction<{
    namespace: string;
    next?: string;
    identifiers: Array<PostDataModel['id']>;
}>('@@posts/SET_POST_COLLECTION');

export const resetPostCollection = createAction<string>('@@posts/RESET_POST_COLLECTION');

interface SetPostListLoadingPayload {
    namespace: string;
    isLoading: boolean;
}

interface SetPostReloadPayload {
    namespace: string;
    reload: boolean;
}

export const setPostCollectionLoading = createAction<SetPostListLoadingPayload>(
    '@@posts/SET_POST_COLLECTION_LOADING',
);

export const setPostCollectionReload = createAction<SetPostReloadPayload>(
    '@@posts/SET_POST_COLLECTION_REALOAD',
);

export const removePostFromBookmarkCollections = createAction<PostDataModel['id']>(
    '@@post/REMOVE_POST_FROM_BOOKMARK_COLLECTIONS',
);

export const addPostToBookmarkCollections = createAction<{
    postId: PostDataModel['id'];
    namespace: string;
}>('@@post/ADD_POST_TO_BOOKMARK_COLLECTIONS');

export function loadPostCollectionAsync(
    namespace: string,
    filters: Parameters<typeof postsService.loadFeed>[0],
): ThunkAction<Promise<void>, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const isLoading = selectPostCollectionLoading(getState(), namespace);

        if (isLoading) {
            return;
        }

        dispatch(setPostCollectionLoading({ namespace, isLoading: true }));

        try {
            const response = await postsService.loadFeed(filters, namespace);

            dispatch(setBusinessRecords(...response.records.business));
            dispatch(setPostRecords(...response.records.posts));
            dispatch(setCategoryRecords(response.records.categories));
            dispatch(
                setPostCollection({
                    namespace,
                    next: response.next,
                    identifiers: response.posts,
                }),
            );
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось загрузить данные.'));
        }

        dispatch(setPostCollectionLoading({ namespace, isLoading: false }));
    };
}

export function removePostsAsync(
    ...identifiers: string[]
): ThunkAction<Promise<boolean>, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            dispatch(removePostRecords(...identifiers));
            await postsService.removePosts(identifiers);

            return true;
        } catch (error) {
            // TODO: подумать над восстановление в удаленного поста в state
            return false;
        }
    };
}

export function reportPostAsync(
    targetId: string,
    reason: ReportReasons,
): ThunkAction<Promise<void>, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            await postsService.reportPost(targetId, reason);

            dispatch(addReportedPost(targetId));
            dispatch(showNotificationAsync('Мы будем показывать меньше таких публикаций.'));
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось отправить жалобу на пост.'));
        }
    };
}

export function loadPostAsync(
    postId: string,
    navigate: NavigateFunction,
): ThunkAction<Promise<void>, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        try {
            const response = await postsService.loadPostById(postId);

            dispatch(setBusinessRecords(...response.records.business));
            dispatch(setCategoryRecords(response.records.categories));
            dispatch(setPostRecords(...response.records.posts));
        } catch (error) {
            navigate('/');
            dispatch(showNotificationAsync('Не удалось загрузить данные.'));
        }
    };
}

export const setPostReaction = createAction<{
    id: PostDataModel['id'];
    reactionState: PostDataModel['reactionState'];
}>('@@posts/SET_REACTION');

export function updatePostReactionAsync({
    postId,
    postNumber,
    collectionId,
    eventSource,
    reactionState,
    preventReactionToggle,
}: {
    postId: string;
    collectionId?: string;
    postNumber: number;
    eventSource: EventSource;
    reactionState: PostModel['reactionState'];
    preventReactionToggle?: boolean;
}): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const model = selectPostById(getState(), postId);

        if (!model) {
            return;
        }

        let currentReactionState = reactionState;

        if (reactionState === model.reactionState && !preventReactionToggle) {
            currentReactionState = ReactionState.NONE;
        }

        dispatch(setPostReaction({ id: postId, reactionState: currentReactionState }));

        try {
            await postsService.updatePostReaction({
                reactionState: currentReactionState,
                model,
                params: { postNumber, eventSource, collectionId },
            });

            if (currentReactionState === 'like') {
                ymTrackPostLike({ own: model.ownersData?.isOwner });
            } else {
                ymTrackPostDislike({ own: model.ownersData?.isOwner });
            }
        } catch (error) {
            dispatch(
                setPostReaction({
                    id: postId,
                    reactionState: model.reactionState,
                }),
            );
        }
    };
}

export function bookmarkPostAsync(
    postId: string,
    collectionId?: string,
): ThunkAction<Promise<boolean>, RootState, undefined, AnyAction> {
    return async (dispatch, getState): Promise<boolean> => {
        const model = selectPostById(getState(), postId);

        if (!model) {
            return false;
        }

        // Мы передаем collectionId только в том случае, если хотим удалить пост из конкретной коллекции,
        // а значит не должны снимать с него букмарк.
        if (!collectionId) {
            dispatch(setPostRecords(toPostDataModel({ ...model, saved: !model.saved })));
        }

        try {
            const updatedDefaultCollections = await postsService.bookmarkPost({
                model,
                collectionId,
            });
            dispatch(setCollectionsRecords(updatedDefaultCollections));
            if (model.saved) {
                dispatch(removePostFromBookmarkCollections(model.id));
            } else {
                ymTrackPostBookmark({ own: model.ownersData?.isOwner });
            }

            return !model.saved;
        } catch (error) {
            dispatch(setPostRecords(toPostDataModel(model)));
            dispatch(showNotificationAsync('Не удалось сохранить пост'));
        }

        return model.saved;
    };
}

export function updatePostStatusAsync(
    identifiers: string[],
    status: PostStatus,
    user?: UserModel,
): ThunkAction<Promise<boolean>, RootState, undefined, AnyAction> {
    return async (dispatch, getState): Promise<boolean> => {
        const currentState = getState();
        const models: PostModel[] = identifiers
            .map((identifier) => selectPostById(currentState, identifier))
            .filter(Boolean) as any;

        if (models.length === 0) {
            return false;
        }

        try {
            await postsService.bulkUpdateStatus(models, status, user);

            dispatch(setPostRecords(...models.map((m) => toPostDataModel({ ...m, status }))));

            if (status === PostStatus.DELETED) {
                dispatch(removePostRecords(...identifiers));
            }
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось обновить статус'));

            return false;
        }

        return true;
    };
}

export function sharePostAsync(postId: string): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const post = selectPostById(getState(), postId);
        if (!post) {
            return;
        }

        try {
            const sharingMethod = await postsService.sharePost(post);

            if (sharingMethod === SharingMethod.CLIPBOARD) {
                dispatch(showNotificationAsync('Скопировано в буфер обмена'));
            }
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось поделиться данными'));
        }
    };
}

export function shareCollectionAsync(
    sharingUrl: string | null,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch) => {
        if (!sharingUrl) {
            return;
        }

        try {
            const sharingMethod = await shareCollection(sharingUrl);

            if (sharingMethod === SharingMethod.CLIPBOARD) {
                dispatch(showNotificationAsync('Скопировано в буфер обмена'));
            }
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось поделиться данными'));
        }
    };
}

export const removeSavedCollectionFromPost = createAction<{
    postId?: PostDataModel['id'];
    collectionId?: PostCollectionModel['id'];
}>('@@posts/REMOVE_SAVED_COLLECTION_FROM_POST');

export function removeSavedCollectionFromPostAsync(
    postId: string,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        try {
            const post = selectPostById(getState(), postId);
            if (!post?.collectionData?.id) {
                return;
            }

            dispatch(removeSavedCollectionFromPost({ postId: post.id }));
        } catch (error) {
            dispatch(showNotificationAsync('Не удалось удалить коллекцию'));
        }
    };
}

export const saveCollectionFromPost = createAction<{
    postId?: PostDataModel['id'];
    collectionId?: PostCollectionModel['id'];
}>('@@posts/SAVE_COLLECTION_FROM_POST');

export const addReportedPost = createAction<string>('@@posts/ADD_REPORTED_POST');

export function saveCollectionFromPostAsync(
    postId: string,
): ThunkAction<void, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        try {
            const post = selectPostById(getState(), postId);
            if (!post?.collectionData?.id) {
                return;
            }

            await dispatch(saveCollectionFromPost({ postId: post.id }));
        } catch (error) {
            await dispatch(showNotificationAsync('Не удалось сохранить коллекцию'));
        }
    };
}

export const setPostBoosting = createAction<{
    id: PostDataModel['id'];
    enabledDefaultBoosting: boolean;
}>('@@posts/SET_POST_BOOSTING');

/**
 * Изменяет состояние активности продвижения поста.
 */
export function updatePostBoostingAsync(
    postId: string,
    enabled: boolean,
): ThunkAction<Promise<boolean>, RootState, undefined, AnyAction> {
    return async (dispatch, getState) => {
        const model = selectPostById(getState(), postId);

        if (!model) {
            return false;
        }

        // Оптимистично обновляем состояние в UI.
        dispatch(setPostBoosting({ id: postId, enabledDefaultBoosting: enabled }));

        try {
            await postsService.updatePostBoosting(postId, enabled);

            return true;
        } catch (error) {
            // В случае ошибки возвращаем предыдущее состояние.
            dispatch(
                setPostBoosting({
                    id: postId,
                    enabledDefaultBoosting: model.enabledDefaultBoosting,
                }),
            );
            dispatch(showNotificationAsync('Не удалось изменить настройки продвижения поста.'));

            return false;
        }
    };
}
